import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import "./Login.css";
import snapgradeLogo from '../../../img/snapgrade-logo.png';
import { API_ENDPOINTS } from '../../../config/api.config';
import { postData } from '../../../utils/apiUtils';

const PasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [countdown, setCountdown] = useState(null);

  // Extract token from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    
    if (!tokenFromUrl) {
      setError("Reset token is missing. Please use the link provided in the email.");
      return;
    }
    
    setToken(tokenFromUrl);
  }, [location.search]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Password validation
  const validatePassword = () => {
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    
    // Validate password
    if (!validatePassword()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Call the API to update the password
      const result = await postData(API_ENDPOINTS.UPDATE_PASSWORD, {
        token: token,
        password: formData.password
      });
      
      setSuccessMessage("Your password has been successfully reset.");
      
      // Start countdown for redirect
      setCountdown(5);
      
      // Redirect to login after 5 seconds
      let count = 5;
      const intervalId = setInterval(() => {
        count -= 1;
        setCountdown(count);
        
        if (count <= 0) {
          clearInterval(intervalId);
          navigate("/login", { 
            state: { success: "Your password has been reset. Please login with your new password." } 
          });
        }
      }, 1000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        setError('Unable to connect to the server. Please try again later.');
      } else if (error.response?.status === 400) {
        setError('Invalid or expired reset token. Please request a new password reset link.');
      } else {
        setError(error.response?.data?.message || 'Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="left-section">
        <img 
          src={snapgradeLogo} 
          alt="SnapGrade Logo" 
          className="logo" 
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
        />
      </div>
      <div className="right-section">
        <div className="login-form-container">
          <h1>Set New Password</h1>
          <p className="subtitle">
            Enter your new password below
          </p>
          
          {error && <div className="error-message">{error}</div>}
          {successMessage && (
            <div className="success-message">
              {successMessage} Redirecting to login in {countdown} seconds...
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                name="password"
                placeholder="Enter new password"
                value={formData.password}
                onChange={handleInputChange}
                className="form-control"
                required
                disabled={!!successMessage}
              />
            </div>
            
            <div className="form-group">
              <label>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="form-control"
                required
                disabled={!!successMessage}
              />
              <small className="password-requirements">
                Password must be at least 8 characters long
              </small>
            </div>
            
            <button 
              type="submit" 
              className="sign-in-button" 
              disabled={isLoading || !!successMessage}
            >
              {isLoading ? "Processing..." : "Reset Password"}
            </button>
            
            <p className="signup-prompt">
              <Link to="/login" className="back-to-login-link">
                Back to login
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset; 